import { getUserBookmarks } from "@/lib/blog/bookmarks";
import { getUserComments } from "@/lib/blog/comments";
import { getReactionTypes, getUserReactions } from "@/lib/blog/reactions";
import { getBlogPost } from "@/lib/blog/static";
import { getBlogPostCommentsUrl, getUrl, reactionToEmoji } from "@/lib/utils";
import { Emoji } from "emoji-picker-react";
import type { ReactNode } from "react";

export interface ProfileActivity {
	id: string;
	type: string;
	action: ReactNode;
	post_id: string;
	link?: {
		title: string;
		url: string;
	};
	comment?: string;
	created_at: string | null;
	deleted_at: string | null;
	updated_at: string | null;
}

interface ProfileActivityProps {
	userId: string;
	sort?: "recent" | "oldest";
	type?: string[];
}

export async function getProfileActivity({
	userId,
	sort = "recent",
	type = ["comment", "reaction", "bookmark"],
}: ProfileActivityProps): Promise<{
	activities: ProfileActivity[];
	error: string | null;
}> {
	const getComments = async (): Promise<ProfileActivity[]> => {
		const { data: comments } = await getUserComments(userId);
		if (!comments) return [];
		const results = await Promise.all(
			comments.map(async (comment) => {
				const { post, error } = await getBlogPost(comment.post_id);
				if (error || !post) return null;
				return {
					id: comment.id,
					type: "comment",
					action: comment.parent_comment_id
						? "replied to a comment on"
						: "commented on",
					post_id: comment.post_id,
					link: {
						title: post.title,
						url: getBlogPostCommentsUrl(post.slug, comment.id),
					},
					comment: comment.comment,
					created_at: comment.created_at,
					deleted_at: comment.deleted_at,
					updated_at: comment.updated_at,
				};
			}),
		);
		return results.filter(Boolean) as ProfileActivity[];
	};

	const getReactions = async (): Promise<ProfileActivity[]> => {
		const { data: reactions } = await getUserReactions(userId);
		if (!reactions) return [];
		const reactionTypes = await getReactionTypes();
		const results = await Promise.all(
			reactions.map(async (reaction) => {
				const { post, error } = await getBlogPost(reaction.post_id);
				if (error || !post) return null;
				const reactionEmoji = reactionToEmoji(
					reactionTypes?.find((type) => type.id === reaction.reaction_type_id)
						?.name ?? "",
				);
				return {
					id: reaction.id,
					type: "reaction",
					action: (
						<span className="flex items-center gap-2">
							reacted
							<Emoji unified={reactionEmoji} size={18} />
							to
						</span>
					),
					post_id: reaction.post_id,
					link: {
						title: post.title,
						url: `${getUrl()}/blog/${post.slug}`,
					},
					created_at: reaction.created_at,
					deleted_at: reaction.deleted_at,
					updated_at: reaction.updated_at,
				};
			}),
		);
		return results.filter(Boolean) as ProfileActivity[];
	};

	const getBookmarks = async (): Promise<ProfileActivity[]> => {
		const { data: bookmarks } = await getUserBookmarks(userId);
		if (!bookmarks) return [];
		const results = await Promise.all(
			bookmarks.map(async (bookmark) => {
				const { post, error } = await getBlogPost(bookmark.post_id);
				if (error || !post) return null;
				return {
					id: bookmark.id,
					type: "bookmark",
					action: "bookmarked",
					post_id: bookmark.post_id,
					link: {
						title: post.title,
						url: `${getUrl()}/blog/${post.slug}`,
					},
					created_at: bookmark.created_at,
					deleted_at: bookmark.deleted_at,
					updated_at: bookmark.updated_at,
				};
			}),
		);
		return results.filter(Boolean) as ProfileActivity[];
	};

	const activities = [
		...(type.includes("comment") ? await getComments() : []),
		...(type.includes("reaction") ? await getReactions() : []),
		...(type.includes("bookmark") ? await getBookmarks() : []),
	].flat();

	if (sort === "oldest") {
		activities.sort((a, b) => {
			const dateA = new Date(a.created_at ?? "").getTime();
			const dateB = new Date(b.created_at ?? "").getTime();
			return dateA - dateB;
		});
	} else {
		activities.sort((a, b) => {
			const dateA = new Date(a.created_at ?? "").getTime();
			const dateB = new Date(b.created_at ?? "").getTime();
			return dateB - dateA;
		});
	}

	return { activities, error: null };
}
